import _ from 'lodash';

import type { ProduitType } from '@/types';
import type { ProduitsListeItemDetailType, ProduitsListeItemType } from '@/types/produit';

/**
 * Calcule le prix unitaire TTC à partir du HT et du taux de TVA (en %).
 * Règle : HT + HT * (tva / 100). Arrondi à 2 décimales.
 */
export const computePrixTTC = (prixHT: number, tva: number): number =>
  Math.round((prixHT || 0) * (1 + (tva || 0) / 100) * 100) / 100;

/**
 * Calcule le prix unitaire HT à partir du TTC et du taux de TVA (en %). Arrondi à 2 décimales.
 */
export const computePrixHT = (prixTTC: number, tva: number): number =>
  Math.round(((prixTTC || 0) / (1 + (tva || 0) / 100)) * 100) / 100;

export type FormatProduitToListeItemType = (produit: ProduitType) => ProduitsListeItemType;

export const formatProduitToListeItem: FormatProduitToListeItemType = (produit) => {
  const {
    identifiant,
    libelle,
    code,
    prixUnitaireHT,
    tva,
    type,
    article,
    codeProduitLocal,
    compteAnalytique,
    fonction,
    ventilation,
    logicielFacturier,
    payableCESU,
    payableChequeVacance,
  } = produit;

  return {
    identifiant,
    libelle,
    code,
    prixUnitaireHT: prixUnitaireHT ?? 0,
    prixUnitaireTTC: computePrixTTC(prixUnitaireHT ?? 0, tva),
    tva,
    type,
    article,
    codeProduitLocal,
    compteAnalytique,
    fonction,
    ventilation,
    source:
      (logicielFacturier?.nom || '') +
      (logicielFacturier?.code ? ` (${logicielFacturier?.code})` : ''),
    payableCESU,
    payableChequeVacance,
  };
};

export const formatProduitsToListeItem = (produits) => _.map(produits, formatProduitToListeItem);

export type FormatProduitToListeItemDetailType = (
  produit: ProduitType,
) => ProduitsListeItemDetailType;

export const formatProduitToListeItemDetail: FormatProduitToListeItemDetailType = (produit) => {
  const {
    identifiant,
    libelle,
    article,
    codeProduitLocal,
    fonction,
    ventilation,
    compteAnalytique,
    compteAnalytique2,
    siret,
    codeCollectivite,
    codeBudget,
  } = produit;

  return {
    identifiant,
    libelle,
    article,
    codeProduitLocal,
    fonction,
    ventilation,
    compteAnalytique,
    compteAnalytique2,
    siret: siret || '',
    codeCollectivite: codeCollectivite || '',
    codeBudget: codeBudget || '',
  };
};

export const formatProduitsToListeItemDetail = (produits) =>
  _.map(produits, formatProduitToListeItemDetail);

/**
 * Retourne le détail d'un produit sous forme de texte,
 * dans le même ordre que la liste de détail des paramètres.
 * Seuls les champs non-vides sont affichés.
 */
export const getDetailProduit = (produit: ProduitType): string => {
  if (!produit) return '';

  const champs: [string, string][] = [
    ['Article', produit.article],
    ['Code produit local', produit.codeProduitLocal],
    ['Fonction', produit.fonction],
    ['Analytique 1', produit.compteAnalytique],
    ['Analytique 2', produit.compteAnalytique2],
    ['Ventilation', produit.ventilation],
    ['Siret', produit.siret],
    ['Code collectivité', produit.codeCollectivite],
    ['Code budget', produit.codeBudget],
  ];

  return champs
    .filter(([, valeur]) => !_.isEmpty(valeur))
    .map(([label, valeur]) => `${label} : ${valeur}`)
    .join('\n');
};
